import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import qs from 'qs';

// Data
import { HOME_ROUTE } from '../../../../../_configuration';
import { buildSetAuthUser } from '../../../../lib/services/auth.service';
import { SessionType } from '../../../../lib/types/session.types';
import { AuthApiService } from '../../../../lib/utils/auth-api.service';
import { VERIFY_USER } from '../../../../lib/queries/user.queries';

// Components
import SetPasswordFormContainer from '../../../User/SetPasswordForm/set-password-form.container';
import SingleFormLayout from '../../_layout/SingleFormLayout/single-form.layout';

interface IVerifyAccountPageProps {
  location: any;
}

type VerifyStatus = 'working' | 'verified' | 'error';

const VerifyAccountPageView: React.FC<IVerifyAccountPageProps> = ({ location }) => {
  const [status, setStatus] = React.useState<VerifyStatus>('working');
  const onSetAuthUser = buildSetAuthUser();
  const [verifyUser] = useMutation(VERIFY_USER);
  const { token, code } = qs.parse(location.search, { ignoreQueryPrefix: true });

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // `code` is the registration verification code (runs the mutation that
      // creates the company); `token` is a legacy direct LOGIN token.
      let loginToken = token ? String(token) : undefined;

      if (!loginToken && code) {
        const { data } = await verifyUser({ variables: { verificationCode: String(code) } });
        loginToken = data?.verifyUser?.token;
      }

      if (!loginToken) throw new Error('Missing verification code.');

      const nextUser: IUser = await AuthApiService.authenticateWithToken({ token: loginToken });
      if (cancelled) return;
      onSetAuthUser(nextUser, SessionType.STAFF);
      setStatus('verified');
    };

    run().catch((error) => {
      console.error('Account verification failed:', error);
      if (!cancelled) setStatus('error');
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'error') {
    return (
      <SingleFormLayout>
        <p className="center">This verification link is invalid or has expired. Please register again or contact support.</p>
      </SingleFormLayout>
    );
  }

  if (status === 'working') {
    return (
      <SingleFormLayout>
        <p className="center">Verifying your account…</p>
      </SingleFormLayout>
    );
  }

  return (
    <SingleFormLayout>
      <p className="center">Email verified. Set your password to sign in</p>
      <SetPasswordFormContainer redirect={HOME_ROUTE} redirectAction="Sign In" />
    </SingleFormLayout>
  );
};

VerifyAccountPageView.propTypes = {
  location: PropTypes.object.isRequired,
};

export const VerifyAccountPage = withRouter(VerifyAccountPageView);
